import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useUserback } from "@userback/react";

/**
 * Builds Userback's `user_data` ({ id, info }) for a logged-in user, or null.
 *
 * index.js passes this to Userback at init and UserbackIdentify passes it to
 * identify(). Both must produce the same value: Userback destroys and rebuilds
 * its launcher whenever the identified user changes, which shows as a flicker.
 */
export const toUserbackUser = (user) => {
  if (!user?._id) return null;

  const first = user.first_name || user.company_first_name || "";
  const last = user.last_name || user.company_last_name || "";
  const name = `${first} ${last}`.trim();

  return {
    id: String(user._id),
    info: {
      name: name || user.email || "Unknown user",
      email: user.email || "",
    },
  };
};

/**
 * Attaches the logged-in user to Userback feedback reports.
 *
 * Renders nothing — it only reacts to auth state. Must live inside both the
 * redux <Provider> and <UserbackProvider>. Without this, feedback still works
 * but arrives anonymous, with no way to tell who reported what.
 */
const UserbackIdentify = () => {
  const user = useSelector((state) => state.auth.user);
  const { identify } = useUserback();

  useEffect(() => {
    const userbackUser = toUserbackUser(user);
    if (!identify || !userbackUser) return;

    try {
      identify(userbackUser.id, userbackUser.info);
    } catch (error) {
      // Never let a feedback-widget failure break the app.
      console.error("Userback identify failed:", error);
    }
  }, [identify, user]);

  return null;
};

export default UserbackIdentify;
